import React from "react";
import { RouteComponentProps } from "react-router";
import { getCancellationPolicyInfo, Icon, IconName } from "halifax";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  CancellationPolicyEnum,
  VIEWED_PACKAGE_CONFIRM_AND_BOOK,
} from "redmond";
import {
  RewardsPaymentSelectors,
  useCheckoutStateSelector,
} from "@capone/checkout";
import { getConfigTenant, isVesperTenant } from "@capone/common";
import { MobileTripReviewConnectorProps } from "./container";
import styles from "./styles.module.scss";
import { ReviewFlightItinerary } from "../../../itinerary-review/components/ReviewFlightItinerary";
import { ReviewHotelItinerary } from "../../../itinerary-review/components/ReviewHotelItinerary";
import {
  FLIGHT_DETAILS_HEADING_TEXT,
  HOTEL_DETAILS_HEADING_TEXT,
  TRIP_REVIEW_HEADING_TEXT,
  TRIP_REVIEW_SUBTITLE_TEXT,
} from "./textConstants";
import { FlightBookPassengerSelection } from "../FlightBookPassengerSelection";
import { PackagesContactInfo } from "../ContactInfo";
import { PackagesPriceBreakdown } from "../PriceBreakdown";
import { ConnectedPackageDiscountBanner } from "../PackageDiscountBanner";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  useExperiments,
} from "../../../../context/experiments";
import { PackageCheckoutDetails } from "../PackageCheckoutDetails";
import { ANCILLARY_DISCLAIMER_TEXT } from "../../../search/components/PackagesInfoModal/textConstants";
import { trackEvent } from "../../../../api/v0/trackEvent";
import {
  getAllSelectedTravelers,
  getPackagesShopPricing,
} from "../../state/selectors/common";

export interface IMobileTripReviewProps
  extends MobileTripReviewConnectorProps,
    RouteComponentProps {}

export const MobileTripReview: React.FC<IMobileTripReviewProps> = ({
  chosenProduct,
}) => {
  const packagesShopPricing = useCheckoutStateSelector(getPackagesShopPricing);
  const packageShopDiscount = packagesShopPricing?.totalPackageSavings.fiat;

  const earnForSelectedCard = useCheckoutStateSelector(
    RewardsPaymentSelectors.getEarnForSelectedRewardsAccount
  );
  const earnForHighestEarnAccount = useCheckoutStateSelector(
    RewardsPaymentSelectors.getEarnForHighestEarnRewardsAccount
  );
  const earnToUse = earnForSelectedCard || earnForHighestEarnAccount;

  const discount = packageShopDiscount;

  const expState = useExperiments();

  const isVesper = isVesperTenant(getConfigTenant());

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  React.useEffect(() => {
    const valueProp = (() => {
      switch (true) {
        case !!earnToUse?.value && !!discount?.value:
          return "earn and package savings";
        case !!earnToUse?.value:
          return "earn only";
        case !!discount?.value:
          return "package savings only";
        default:
          return "none";
      }
    })();

    const cancellationPolicyInfo = chosenProduct?.cancellationPolicy
      ? getCancellationPolicyInfo(chosenProduct?.cancellationPolicy)
      : null;

    trackEvent({
      eventName: VIEWED_PACKAGE_CONFIRM_AND_BOOK,
      properties: {
        val_prop: valueProp,
        hotel_cancellation: !!(
          cancellationPolicyInfo &&
          chosenProduct?.cancellationPolicy.CancellationPolicy !==
            CancellationPolicyEnum.NonRefundable
        ),
      },
    });
  }, []);

  const guests = useCheckoutStateSelector(getAllSelectedTravelers);

  return (
    <Box className={clsx(styles["packages-checkout-trip-review"], {
      [styles["redesigned"]]: isVesper,
    })}>
      {isPackageCheckoutOptimizationExperiment ? undefined : (
        <>
          <Typography className={styles["trip-review-heading"]} variant="h2">
            {TRIP_REVIEW_HEADING_TEXT}
          </Typography>
          <Typography className={styles["trip-review-subtitle"]}>
            {TRIP_REVIEW_SUBTITLE_TEXT}
          </Typography>
        </>
      )}

      <ConnectedPackageDiscountBanner
        isMobile
        isPackageCheckoutOptimizationExperiment={
          isPackageCheckoutOptimizationExperiment
        }
      />

      {isPackageCheckoutOptimizationExperiment ? (
        <PackageCheckoutDetails />
      ) : (
        <>
          <Box className={styles["hotel-itinerary-header"]}>
            <Icon name={IconName.HotelFunnelIcon} />
            <Typography className={styles["header-text"]} variant="h3">
              {HOTEL_DETAILS_HEADING_TEXT}
            </Typography>
          </Box>
          <ReviewHotelItinerary
            showCardContentIcons={false}
            adultsCount={guests?.adultCount}
            childrenAges={guests?.children}
          />
          <Box className={styles["flight-itinerary-header"]}>
            <Icon name={IconName.FlightFunnelIcon} />
            <Typography className={styles["header-text"]} variant="h3">
              {FLIGHT_DETAILS_HEADING_TEXT}
            </Typography>
          </Box>
          <ReviewFlightItinerary />
        </>
      )}

      <FlightBookPassengerSelection isMobile />
      <PackagesContactInfo isMobile />

      {isPackageCheckoutOptimizationExperiment ? (
        <div className={styles["terms-container"]}>
          <Typography className={styles["terms-text"]}>
            {ANCILLARY_DISCLAIMER_TEXT}
          </Typography>
        </div>
      ) : undefined}

      <PackagesPriceBreakdown isMobile />
    </Box>
  );
};
